import { CoinIcon } from '../components/ui/CoinIcon';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useState } from 'react';
import axios from 'axios';
import { Key, Save, Eye, EyeOff, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => new Intl.NumberFormat('id-ID').format(n);

const COIN_PACKAGES_DEFAULT = [
  { id: 'starter',  label: 'Pemula',  coins: 15,  bonus: 0,  price: 37500 },
  { id: 'standard', label: 'Standar', coins: 35,  bonus: 5,  price: 80000 },
  { id: 'hemat',    label: 'Hemat',   coins: 65,  bonus: 15, price: 140000 },
  { id: 'pro',      label: 'Pro',     coins: 120, bonus: 30, price: 240000 },
];

export const Settings = () => {
  const [anthropicKey, setAnthropicKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveKey = async () => {
    if (!anthropicKey.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings/anthropic-key`, { key: anthropicKey }, { withCredentials: true });
      setKeySaved(true);
      setAnthropicKey('');
      setTimeout(() => setKeySaved(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Gagal menyimpan key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pengaturan
          </h1>
          <p className="text-slate-500 text-sm mt-1">Konfigurasi sistem Rencana Guru</p>
        </div>

        {/* AI Key */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              <Key size={18} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Anthropic API Key</h2>
              <p className="text-xs text-slate-400">Untuk generate dokumen dengan Claude AI</p>
            </div>
          </div>

          <div className="relative mb-3">
            <input
              type={showKey ? 'text' : 'password'}
              value={anthropicKey}
              onChange={e => setAnthropicKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              className="w-full border border-slate-200 rounded-xl px-4 py-3 pr-12 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button onClick={() => setShowKey(!showKey)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-5">
            Key saat ini tersimpan di server. Isi field ini hanya jika ingin menggantinya.
            Dapatkan key di <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">console.anthropic.com</a>
          </p>

          <button onClick={saveKey} disabled={saving || !anthropicKey.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:shadow-md disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
            {keySaved ? <><CheckCircle size={15} /> Tersimpan!</> : saving ? 'Menyimpan...' : <><Save size={15} /> Simpan API Key</>}
          </button>
        </div>

        {/* Coin Packages Info */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-yellow-100 flex items-center justify-center"><CoinIcon size={20} /></div>
            <div>
              <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Paket Coin Guru</h2>
              <p className="text-xs text-slate-400">Konfigurasi paket topup yang tersedia</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            {COIN_PACKAGES_DEFAULT.map(pkg => {
              const total = pkg.coins + pkg.bonus;
              return (
                <div key={pkg.id} className="border border-slate-100 rounded-xl p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-slate-700 text-sm">{pkg.label}</span>
                    {pkg.bonus > 0 && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">+{pkg.bonus} bonus</span>
                    )}
                  </div>
                  <div className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {total} koin
                  </div>
                  <div className="text-sm text-slate-600 font-semibold">Rp {fmt(pkg.price)}</div>
                  <div className="text-xs text-slate-400 mt-1">Rp {fmt(Math.round(pkg.price / total))}/koin</div>
                </div>
              );
            })}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
            <p className="text-xs text-blue-700">
              <strong>Aturan koin:</strong> Daftar baru = 10 koin gratis · Pakai kode referral = +5 koin · Referrer dapat +10 koin · 1 Coin Guru = Rp 1.500
            </p>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
};
